import React, {useEffect, useRef, useState} from "react";
import AudioMotionAnalyzer from 'audiomotion-analyzer';
import {getAudioContext} from '../services/audioContext';

interface AudioMotionSoundVisualizerProps {
    /** Playable sound URL — a prefetched blob URL or a direct file URL. */
    soundUrl: string;
    /** Cuts playback off after this many seconds — the Friday hard-mode
     * safety net when a car has no trimmed 1-second clip. */
    maxSeconds?: number;
}

/** A media element can only ever bind to one MediaElementSourceNode, so the
 * analyzer is created once per <audio> element and cached on it (React 18
 * StrictMode re-runs effects against the same DOM element in development). */
type AnalyzedAudioElement = HTMLAudioElement & {audioMotion?: AudioMotionAnalyzer};

export const AudioMotionSoundVisualizer = ({soundUrl, maxSeconds}: AudioMotionSoundVisualizerProps) => {
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [showReplay, setShowReplay] = useState(false);
    /** True when the browser rejected play() (e.g. iOS autoplay policy): show a tap prompt. */
    const [blocked, setBlocked] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const audioRef = useRef<AnalyzedAudioElement | null>(null);

    useEffect(() => {
        if (audioPlaying && audioRef.current) {
            audioRef.current.play()
                .then(() => {
                    setShowReplay(true);
                    setBlocked(false);
                })
                .catch(() => {
                    setAudioPlaying(false);
                    setBlocked(true);
                });
        }
    }, [audioPlaying]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || maxSeconds === undefined) {
            return;
        }
        let cutoff: ReturnType<typeof setTimeout>;
        const onPlaying = () => {
            clearTimeout(cutoff);
            cutoff = setTimeout(() => {
                audio.pause();
                // Rewind so a replay also starts (and gets cut) from the top.
                audio.currentTime = 0;
                setAudioPlaying(false);
            }, Math.max(0, (maxSeconds - audio.currentTime) * 1000));
        };
        const onPause = () => clearTimeout(cutoff);
        audio.addEventListener('playing', onPlaying);
        audio.addEventListener('pause', onPause);
        return () => {
            clearTimeout(cutoff);
            audio.removeEventListener('playing', onPlaying);
            audio.removeEventListener('pause', onPause);
        };
    }, [maxSeconds]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        if (containerRef.current && !audio.audioMotion) {
            audio.audioMotion =
                new AudioMotionAnalyzer(
                    containerRef.current,
                    {
                        source: audio,
                        // Shared, tap-unlocked context (a per-question context starts suspended on iOS).
                        audioCtx: getAudioContext(),
                        "alphaBars": false,
                        "ansiBands": true,
                        "barSpace": 0.5,
                        "bgAlpha": 0.7,
                        "channelLayout": "single",
                        "colorMode": "gradient",
                        "fadePeaks": false,
                        "fftSize": 8192,
                        "fillAlpha": 1,
                        "frequencyScale": "log",
                        "gradient": "classic",
                        "gravity": 3.8,
                        "ledBars": true,
                        "linearAmplitude": false,
                        "linearBoost": 1,
                        "lineWidth": 0,
                        "loRes": false,
                        "lumiBars": false,
                        "maxDecibels": -25,
                        "maxFPS": 0,
                        "maxFreq": 16000,
                        "minDecibels": -85,
                        "minFreq": 25,
                        "mirror": 0,
                        "mode": 8,
                        "noteLabels": false,
                        "outlineBars": true,
                        "overlay": false,
                        "peakFadeTime": 750,
                        "peakHoldTime": 500,
                        "peakLine": true,
                        "radial": false,
                        "radialInvert": false,
                        "radius": 0.3,
                        "reflexAlpha": 0.15,
                        "reflexBright": 1,
                        "reflexFit": true,
                        "reflexRatio": 0,
                        "roundBars": false,
                        "showBgColor": true,
                        "showFPS": false,
                        "showPeaks": true,
                        "showScaleX": false,
                        "showScaleY": false,
                        "smoothing": 0.5,
                        "spinSpeed": 0,
                        "splitGradient": false,
                        "trueLeds": true,
                        "useCanvas": true,
                        "volume": 1,
                        "weightingFilter": ""
                    });
        }

        const onEnded = () => setAudioPlaying(false);
        audio.addEventListener('ended', onEnded);

        setAudioPlaying(true);

        // The analyzer is NOT destroyed here: destroying leaves the audio
        // element bound to a dead source node, and it is garbage-collected
        // together with the element when the component unmounts for real.
        return () => {
            audio.removeEventListener('ended', onEnded);
        };
    }, []);

    const onStart = () => {
        setAudioPlaying(true);
    };

    return (
        <div className={'visualizer'} onClick={onStart}>
            {/* crossOrigin lets Web Audio analyze CORS-enabled remote files; blob URLs are same-origin. */}
            <audio ref={audioRef} src={soundUrl} crossOrigin={'anonymous'}/>
            {!audioPlaying && blocked &&
                <button className={"replay-button"} onClick={onStart}>&nbsp;<br/>TAP TO HEAR</button>
            }
            {!audioPlaying && showReplay && !blocked &&
                <button className={"replay-button"} onClick={onStart}>&nbsp;<br/>REPLAY?</button>
            }
            <div id={"vis"} ref={containerRef} hidden={!audioPlaying}>
            </div>
        </div>
    );
};
